import {
  createInertiaApp,
  router,
  type ResolvedComponent,
} from "@inertiajs/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { createRoot } from "react-dom/client";
import Layout from "../layouts/Layout";
import theme from "../theme";
import "../styles/a11y.css";

import store from "@frontend/redux/store";
import { WeatherInitializer } from "@frontend/redux/weather/WeatherInitializer";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isLeapYear from "dayjs/plugin/isLeapYear";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Provider } from "react-redux";
import { logDev } from "@frontend/utils";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isLeapYear);
dayjs.extend(advancedFormat);

interface PageModule {
  default: ResolvedComponent;
}

const pages = import.meta.glob<PageModule>("../pages/**/*.tsx");

document.addEventListener("DOMContentLoaded", () => {
  // After client-side navigation, move focus to <main> so keyboard users
  // and screen readers don't stay parked on the previous page's element.
  router.on("navigate", () => {
    requestAnimationFrame(() => {
      const main = document.getElementById("main-content");
      if (main) {
        main.focus({ preventScroll: false });
      }
    });
  });

  createInertiaApp({
    // Set default page title
    // see https://inertia-rails.dev/guide/title-and-meta
    //
    // title: title => title ? `${title} - App` : 'App',

    // Disable progress bar
    //
    // see https://inertia-rails.dev/guide/progress-indicators
    // progress: false,

    resolve: async (name) => {
      const _page = pages[`../pages/${name}/index.tsx`];
      if (!_page) {
        console.error(`Missing Inertia page component: '${name}.tsx'`);
      }

      const page = await _page!();

      // To use a default layout, import the Layout component
      // and use the following line.
      // see https://inertia-rails.dev/guide/pages#default-layouts
      page.default.layout = page.default.layout ?? Layout;

      return page.default;
    },

    setup({ el, App, props }) {
      if (el) {
        logDev("Inertia app setup", { el, props: props.initialPage.props });
        createRoot(el!).render(
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <WeatherInitializer />
              <CssBaseline />
              <App {...props} />
            </Provider>
          </ThemeProvider>,
        );
      } else {
        console.error(
          "Missing root element.\n\n" +
            "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
            'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
        );
      }
    },
  });
});
